import React, { useState, useEffect, useMemo } from 'react';
import { getAiTestDefinitions, runAiTestSuite, getAiTestRuns, getAiTestRun } from '../utils/api';
import ModelSelect from '../components/ModelSelect';

const REQUIRED_TEST_ORDER = ['chat_simple', 'chat_with_text_file', 'chat_with_pdf', 'chat_with_image', 'embeddings_simple'];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.slice(commaIdx + 1) : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function toDisplayValue(value) {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function inferProviderSlugFromModel(modelMeta, fallbackModelId = '') {
  const candidates = [
    modelMeta?.providerSlug,
    modelMeta?.providerId,
    modelMeta?.provider,
  ].map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
  if (candidates.length > 0) return candidates[0];

  const id = String(fallbackModelId || '').trim().toLowerCase();
  if (!id) return '';
  if (id.startsWith('gpt-') || /^o\d/.test(id)) return 'openai';
  if (id.startsWith('claude')) return 'anthropic';
  if (id.startsWith('gemini')) return 'google';
  if (id.startsWith('llama') || id.startsWith('mistral') || id.startsWith('qwen')) return 'ollama';
  if (id.startsWith('pansynapse')) return 'pansynapse';
  return '';
}

function getResultState(result = {}) {
  if (result?.metadata?.skipped === true) return 'skipped';
  if (result?.passed === true || result?.status === 'passed') return 'passed';
  return 'failed';
}

function getResultReason(result = {}) {
  return result?.error_message
    || result?.error
    || result?.metadata?.skipReason
    || (getResultState(result) === 'passed' ? 'Passed' : 'Failed without explicit reason');
}

function toDebugJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function getResultDebugPayload(result = {}) {
  return {
    status: result?.status || null,
    state: getResultState(result),
    reason: getResultReason(result),
    expected: result?.expected ?? result?.expected_output ?? null,
    actual: result?.actual ?? result?.actual_output ?? null,
    metadata: result?.metadata || null,
    durationMs: result?.duration_ms ?? result?.durationMs ?? null,
  };
}

function AiAdminCapabilityTestsPanel({ setMessage }) {
  const [loading, setLoading] = useState(true);
  const [, setLoadError] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runResults, setRunResults] = useState(null);
  const [llmModelId, setLlmModelId] = useState('');
  const [embeddingModelId, setEmbeddingModelId] = useState('');
  const [llmModelMeta, setLlmModelMeta] = useState(null);
  const [embeddingModelMeta, setEmbeddingModelMeta] = useState(null);
  const [running, setRunning] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [fileOverrides, setFileOverrides] = useState({});
  const [embeddingText, setEmbeddingText] = useState('hello world');
  const [embeddingExpectedDimensions, setEmbeddingExpectedDimensions] = useState('');
  const [runsPage, setRunsPage] = useState(1);

  const orderedDefinitions = useMemo(() => {
    const defs = Array.isArray(definitions) ? [...definitions] : [];
    const rank = new Map(REQUIRED_TEST_ORDER.map((slug, idx) => [slug, idx]));
    defs.sort((a, b) => {
      const aSlug = String(a?.slug || '');
      const bSlug = String(b?.slug || '');
      const aRank = rank.has(aSlug) ? rank.get(aSlug) : 1000;
      const bRank = rank.has(bSlug) ? rank.get(bSlug) : 1000;
      if (aRank !== bRank) return aRank - bRank;
      return aSlug.localeCompare(bSlug);
    });
    return defs;
  }, [definitions]);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const [definitionsResult, runsResult] = await Promise.all([
        getAiTestDefinitions().catch(err => ({ definitions: [] })),
        getAiTestRuns(runsPage, 10).catch(err => ({ runs: [] }))
      ]);

      const loadedDefinitions = definitionsResult.definitions || [];
      setDefinitions(loadedDefinitions);
      setRuns(runsResult.runs || []);
      setSelectedTests(prev => {
        const available = new Set(loadedDefinitions.map(def => String(def.slug || '').trim()).filter(Boolean));
        if (!available.size) return [];
        if (prev.length > 0) {
          const filtered = prev.filter(slug => available.has(slug));
          if (filtered.length > 0) return filtered;
        }
        return Array.from(available);
      });

    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedDefinitions = useMemo(() => {
    const selected = new Set(selectedTests.map((slug) => String(slug || '').trim()).filter(Boolean));
    return orderedDefinitions.filter((def) => selected.has(String(def?.slug || '').trim()));
  }, [orderedDefinitions, selectedTests]);

  const needsEmbeddingModel = useMemo(
    () => selectedDefinitions.some((def) => String(def?.test_type || '').trim().toLowerCase() === 'embed'),
    [selectedDefinitions]
  );
  const needsLlmModel = useMemo(
    () => selectedDefinitions.some((def) => String(def?.test_type || '').trim().toLowerCase() !== 'embed'),
    [selectedDefinitions]
  );

  const llmProviderSlug = useMemo(
    () => inferProviderSlugFromModel(llmModelMeta, llmModelId),
    [llmModelMeta, llmModelId]
  );
  const embeddingProviderSlug = useMemo(
    () => inferProviderSlugFromModel(embeddingModelMeta, embeddingModelId),
    [embeddingModelMeta, embeddingModelId]
  );

  const handleToggleTest = (slug) => {
    const testSlug = String(slug || '').trim();
    if (!testSlug) return;
    setSelectedTests(prev => (
      prev.includes(testSlug)
        ? prev.filter(item => item !== testSlug)
        : [...prev, testSlug]
    ));
  };

  const handleSelectAllTests = () => {
    setSelectedTests(orderedDefinitions.map(def => String(def.slug || '').trim()).filter(Boolean));
  };

  const handleClearTests = () => {
    setSelectedTests([]);
  };

  const handleFileOverrideChange = async (slug, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      const base64Data = await fileToBase64(file);
      setFileOverrides(prev => ({
        ...prev,
        [slug]: {
          name: file.name,
          mime_type: file.type || 'application/octet-stream',
          data: base64Data,
          encoding: 'base64'
        }
      }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message || `Failed to read file for ${slug}` });
    }
  };

  const getRunOptions = () => {
    const selected = selectedTests.map(slug => String(slug || '').trim()).filter(Boolean);
    const testOverrides = {};

    ['chat_with_text_file', 'chat_with_pdf', 'chat_with_image'].forEach((slug) => {
      if (selected.includes(slug) && fileOverrides[slug]?.data) {
        testOverrides[slug] = { file: fileOverrides[slug] };
      }
    });

    if (selected.includes('embeddings_simple')) {
      const emb = {
        text: embeddingText || 'hello world',
        model_id: embeddingModelId || llmModelId,
      };
      if (embeddingProviderSlug) {
        emb.provider_slug = embeddingProviderSlug;
      }
      const expected = Number(embeddingExpectedDimensions);
      if (Number.isFinite(expected) && expected > 0) {
        emb.expected_dimensions = expected;
      }
      testOverrides.embeddings_simple = emb;
    }

    return {
      selectedTests: selected,
      ...(Object.keys(testOverrides).length > 0 ? { testOverrides } : {})
    };
  };

  const handleRunSuite = async () => {
    if (!selectedTests.length) {
      setMessage({ type: 'error', text: 'Select at least one test to run' });
      return;
    }
    if (needsLlmModel && !llmModelId) {
      setMessage({ type: 'error', text: 'Select an LLM model to run chat/text/file tests' });
      return;
    }
    if (needsEmbeddingModel && !embeddingModelId) {
      setMessage({ type: 'error', text: 'Select an embedding model to run embedding tests' });
      return;
    }

    const defaultModelId = llmModelId || embeddingModelId;
    const defaultProviderSlug = llmProviderSlug || embeddingProviderSlug;
    if (!defaultModelId || !defaultProviderSlug) {
      setMessage({ type: 'error', text: 'Unable to infer provider/model for this run' });
      return;
    }

    try {
      setRunning(true);
      setMessage(null);

      const result = await runAiTestSuite(defaultProviderSlug, defaultModelId, getRunOptions());
      setMessage({ type: 'success', text: `Started ${selectedTests.length} test(s) (Run ID: ${result.run?.id})` });

      const updatedRuns = await getAiTestRuns(1, 10);
      setRuns(updatedRuns.runs || []);
      setRunsPage(1);

      pollForRunCompletion(result.run?.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setRunning(false);
    }
  };

  const pollForRunCompletion = async (runId) => {
    const pollInterval = setInterval(async () => {
      try {
        const runData = await getAiTestRun(runId);
        const run = runData.run;
        if (run && (run.status === 'completed' || run.status === 'failed')) {
          clearInterval(pollInterval);
          const updatedRuns = await getAiTestRuns(1, 10);
          setRuns(updatedRuns.runs || []);
          setRunsPage(1);
          setMessage({ type: run.status === 'completed' ? 'success' : 'error', text: `Test suite ${run.status}` });
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    setTimeout(() => clearInterval(pollInterval), 120000);
  };

  const handleSelectRun = async (runId) => {
    try {
      setSelectedRun(runId);
      const runData = await getAiTestRun(runId);
      setRunResults(runData);
    } catch (err) {
      console.error('Error loading run details:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status, run = null) => {
    const classes = {
      pending: 'status-pending',
      running: 'status-running',
      completed: 'status-completed',
      failed: 'status-failed'
    };

    const normalizedStatus = String(status || '').toLowerCase();
    const total = Number(run?.total_tests ?? 0);
    const passed = Number(run?.passed_count ?? 0);
    const hasCounts = Number.isFinite(total) && total > 0 && Number.isFinite(passed) && passed >= 0;
    const label = normalizedStatus ? normalizedStatus.toUpperCase() : 'UNKNOWN';
    const text = hasCounts ? `${label} [${passed}/${total}]` : label;

    return <span className={`status-badge ${classes[normalizedStatus] || ''}`}>{text}</span>;
  };

  const embeddingDimensionRows = useMemo(() => {
    if (!runResults?.results?.length) return [];
    return runResults.results
      .map((result) => {
        const expectedPayload = result?.expected ?? result?.expected_output;
        const actualPayload = result?.actual ?? result?.actual_output;
        const actualDim = Number(actualPayload?.dimensions || 0);
        const expectedDim = Number(expectedPayload?.dimensions || 0);
        if (!actualDim && !expectedDim) return null;
        return {
          testName: result.test_name || result.ai_test_definitions?.name || result.test_slug || 'embedding',
          expected: expectedDim || null,
          actual: actualDim || null
        };
      })
      .filter(Boolean);
  }, [runResults]);

  return (
    <div className="ai-capability-tests-panel">
      {loading ? (
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading...
        </div>
      ) : (
        <>
          <div className="test-runner-panel">
            <h3>
              <i className="fas fa-play"></i> Run Tests
            </h3>

            <div className="run-controls">
              <div className="form-group">
                <label>LLM Model</label>
                <ModelSelect
                  value={llmModelId}
                  onChange={setLlmModelId}
                  onModelChange={setLlmModelMeta}
                  modelType="llm"
                  disabled={running}
                  placeholder="Select an LLM model"
                />
              </div>

              <div className="form-group">
                <label>Embedding Model</label>
                <ModelSelect
                  value={embeddingModelId}
                  onChange={setEmbeddingModelId}
                  onModelChange={setEmbeddingModelMeta}
                  modelType="embedding"
                  disabled={running}
                  placeholder="Select an embedding model"
                />
              </div>

              <button
                className="btn btn-primary"
                onClick={handleRunSuite}
                disabled={
                  running
                  || selectedTests.length === 0
                  || (needsLlmModel && !llmModelId)
                  || (needsEmbeddingModel && !embeddingModelId)
                }
              >
                {running ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Running...
                  </>
                ) : (
                  <>
                    <i className="fas fa-play"></i> Run Selected ({selectedTests.length})
                  </>
                )}
              </button>
            </div>

            <div className="runner-notes">
              <span>Pick one test for isolated runs or multiple tests for a mini-suite. LLM and embedding models are selected independently.</span>
            </div>
          </div>

          <div className="test-definitions-panel">
            <h3>
              <i className="fas fa-list-ul"></i> Test Definitions ({definitions.length})
            </h3>
            {definitions.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-flask"></i>
                <p>No test definitions found</p>
              </div>
            ) : (
              <div className="definitions-list">
                <div className="definitions-toolbar">
                  <button type="button" className="btn btn-sm btn-secondary" onClick={handleSelectAllTests} disabled={running}>Select All</button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={handleClearTests} disabled={running}>Clear</button>
                </div>
                {orderedDefinitions.map(def => (
                  <div key={def.id} className="definition-item">
                    <div className="definition-header">
                      <label className="definition-select">
                        <input
                          type="checkbox"
                          checked={selectedTests.includes(def.slug)}
                          onChange={() => handleToggleTest(def.slug)}
                          disabled={running}
                        />
                        <span className="definition-name">{def.name}</span>
                      </label>
                      <span className={`definition-type type-${def.test_type}`}>{def.test_type}</span>
                    </div>
                    <div className="definition-description">{def.description}</div>
                    {(def.slug === 'chat_with_text_file' || def.slug === 'chat_with_pdf' || def.slug === 'chat_with_image') && (
                      <div className="definition-override-row">
                        <input
                          type="file"
                          onChange={(event) => handleFileOverrideChange(def.slug, event)}
                          accept={def.slug === 'chat_with_pdf' ? '.pdf,application/pdf' : def.slug === 'chat_with_image' ? 'image/*' : '.txt,.md,text/*'}
                          disabled={running || !selectedTests.includes(def.slug)}
                        />
                        <span className="file-name">{fileOverrides[def.slug]?.name || 'Using default sample file'}</span>
                      </div>
                    )}
                    {def.slug === 'embeddings_simple' && (
                      <div className="definition-override-grid">
                        <input
                          type="text"
                          value={embeddingText}
                          onChange={(e) => setEmbeddingText(e.target.value)}
                          placeholder="Embedding test text"
                          disabled={running || !selectedTests.includes(def.slug)}
                        />
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={embeddingExpectedDimensions}
                          onChange={(e) => setEmbeddingExpectedDimensions(e.target.value)}
                          placeholder="Expected dimensions (optional)"
                          disabled={running || !selectedTests.includes(def.slug)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="test-runs-panel">
            <h3>
              <i className="fas fa-history"></i> Run History (page {runsPage})
            </h3>
            {runs.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-history"></i>
                <p>No runs yet</p>
              </div>
            ) : (
              <div className="runs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Run ID</th>
                      <th>Provider</th>
                      <th>Model</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map(run => (
                      <tr key={run.id} className={selectedRun === run.id ? 'selected' : ''}>
                        <td className="run-id">{(run.id || '').substring(0, 8)}...</td>
                        <td>{run.provider_slug}</td>
                        <td>{run.model_id}</td>
                        <td>{getStatusBadge(run.status, run)}</td>
                        <td>{formatDate(run.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleSelectRun(run.id)}
                          >
                            <i className="fas fa-eye"></i> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="runs-pagination">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      if (runsPage <= 1) return;
                      const newPage = runsPage - 1;
                      setRunsPage(newPage);
                      getAiTestRuns(newPage, 10).then(r => setRuns(r.runs || [])).catch(() => {});
                    }}
                    disabled={runsPage <= 1}
                  >
                    <i className="fas fa-arrow-left"></i> Newer
                  </button>
                  <span>Page {runsPage}</span>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const newPage = runsPage + 1;
                      setRunsPage(newPage);
                      getAiTestRuns(newPage, 10).then(r => setRuns(r.runs || [])).catch(() => {});
                    }}
                  >
                    Older <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {selectedRun && runResults && (
            <div className="test-results-panel">
              <h3>
                <i className="fas fa-clipboard-check"></i> Run Results
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setSelectedRun(null);
                    setRunResults(null);
                  }}
                  style={{ marginLeft: '12px' }}
                >
                  <i className="fas fa-times"></i> Close
                </button>
              </h3>

              <div className="run-summary">
                <div className="summary-row">
                  <span className="summary-label">Run ID:</span>
                  <span className="summary-value">{runResults.run?.id}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Provider:</span>
                  <span className="summary-value">{runResults.run?.provider_slug}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Model:</span>
                  <span className="summary-value">{runResults.run?.model_id}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Status:</span>
                  {getStatusBadge(runResults.run?.status, runResults.run)}
                </div>
                <div className="summary-row">
                  <span className="summary-label">Created:</span>
                  <span className="summary-value">{formatDate(runResults.run?.created_at)}</span>
                </div>
                {runResults.run?.error_message && (
                  <div className="summary-row error">
                    <span className="summary-label">Error:</span>
                    <span className="summary-value">{runResults.run?.error_message}</span>
                  </div>
                )}
              </div>

              {embeddingDimensionRows.length > 0 && (
                <div className="embedding-dimensions-panel">
                  <h4>Embedding Size Check</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Expected</th>
                        <th>Actual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {embeddingDimensionRows.map((row, idx) => (
                        <tr key={`${row.testName}-${idx}`}>
                          <td>{row.testName}</td>
                          <td>{row.expected || 'N/A'}</td>
                          <td>{row.actual || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="results-list">
                <table>
                  <thead>
                    <tr>
                      <th>Test</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Expected</th>
                      <th>Actual</th>
                      <th>Debug</th>
                      <th>Duration (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runResults.results?.map((result, idx) => {
                      const resultState = getResultState(result);
                      return (
                      <tr key={idx} className={resultState}>
                        <td>{result.test_name || result.ai_test_definitions?.name || result.test_slug || 'Test'}</td>
                        <td>
                          <span className={`result-badge ${resultState}`}>
                            <i className={`fas fa-${resultState === 'passed' ? 'check' : (resultState === 'skipped' ? 'forward' : 'times')}`}></i>
                            {' '}{resultState === 'passed' ? 'PASS' : (resultState === 'skipped' ? 'SKIP' : 'FAIL')}
                          </span>
                        </td>
                        <td className="reason">{getResultReason(result)}</td>
                        <td className="expected">{toDisplayValue(result.expected ?? result.expected_output)}</td>
                        <td className="actual">{toDisplayValue(result.actual ?? result.actual_output)}</td>
                        <td className="debug">
                          <details className="result-debug-details">
                            <summary>View</summary>
                            <pre>{toDebugJson(getResultDebugPayload(result))}</pre>
                          </details>
                        </td>
                        <td>{result.duration_ms ?? result.durationMs ?? 'N/A'}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AiAdminCapabilityTestsPanel;
